import type { z } from 'zod'
import { env } from '@/config/env'

export async function loadWithFallback<T>(
  schema: z.ZodType<T>,
  firestoreFetch: () => Promise<unknown>,
  localFetch: () => Promise<unknown>,
  label: string
): Promise<T> {
  const raw = await fetchRaw(firestoreFetch, localFetch, label)
  const parsed = schema.safeParse(raw)

  if (!parsed.success) {
    throw new Error(`Invalid ${label} data: ${parsed.error.message}`)
  }

  return Object.freeze(parsed.data) as T
}

export async function loadCollectionWithFallback<T>(
  schema: z.ZodType<T>,
  firestoreFetch: () => Promise<unknown[]>,
  localFetch: () => Promise<unknown>,
  label: string
): Promise<readonly T[]> {
  const raw = await fetchRaw(firestoreFetch, localFetch, label)
  const arr = Array.isArray(raw) ? raw : [raw]

  const results: T[] = []
  for (const item of arr) {
    const parsed = schema.safeParse(item)
    if (!parsed.success) {
      if (env.features.devLogging) {
        // eslint-disable-next-line no-console
        console.warn(`[contentProvider] Skipping invalid ${label} item:`, parsed.error.message)
      }
      continue
    }
    results.push(Object.freeze(parsed.data) as T)
  }

  return Object.freeze(results)
}

async function fetchRaw(
  firestoreFetch: () => Promise<unknown>,
  localFetch: () => Promise<unknown>,
  label: string
): Promise<unknown> {
  if (!env.features.firebaseCms) {
    return localFetch()
  }

  try {
    return await firestoreFetch()
  } catch (error) {
    if (env.features.devLogging) {
      // eslint-disable-next-line no-console
      console.warn(`[contentProvider] Firestore fetch failed for ${label}, falling back to local:`, error)
    }
    return localFetch()
  }
}
